import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AlertTriangle, Clock } from "lucide-react";
import GenericCard from "@ui-components/Card/GenericCard";
import {
  axisTicks,
  bucketWidthMs,
  findGaps,
  findPeak,
  formatClock,
  formatSpan,
  intervalLabel,
  legendLevels,
  normalizeBuckets,
  rangeBetweenBuckets,
  RawVolumeBucket,
  VOLUME_LEVELS,
  VolumeLevel,
  VolumeBucket,
  VolumeRange,
} from "./volume";

/** Stacked bar colours; the semantic tokens flip with the theme. */
const LEVEL_BG_CLASS: Record<VolumeLevel, string> = {
  TRACE: "bg-semantic-bg-secondary",
  DEBUG: "bg-semantic-border-tertiary",
  INFO: "bg-semantic-bg-brand-bold",
  WARNING: "bg-semantic-bg-warning-bold",
  ERROR: "bg-semantic-bg-danger-bold",
};

interface LogsVolumeHistogramProps {
  /** The backend's histogram of the first page. Null or empty: nothing to draw. */
  buckets: RawVolumeBucket[] | null;
  /** The range the table is showing, for the "Showing" chip. */
  range: VolumeRange;
  /** A drag (or a click on one bar) over the bars, edges on bucket borders. */
  onRangeSelect: (range: VolumeRange) => void;
}

interface Selection {
  anchor: number;
  current: number;
}

const CHART_HEIGHT_CLASS = "h-24";

/** "N logs · X ERROR · Y WARNING · Z INFO" of the buckets, or null without buckets. */
export function countsSummary(rows: VolumeBucket[]): string | null {
  if (rows.length === 0) return null;
  const sum = (pick: (row: VolumeBucket) => number) =>
    rows.reduce((acc, row) => acc + pick(row), 0).toLocaleString();
  return [
    `${sum((row) => row.total)} logs`,
    `${sum((row) => row.counts.ERROR)} ERROR`,
    `${sum((row) => row.counts.WARNING)} WARNING`,
    `${sum((row) => row.counts.INFO)} INFO`,
  ].join(" · ");
}

export const LogsVolumeHistogram: React.FC<LogsVolumeHistogramProps> = ({
  buckets,
  range,
  onRangeSelect,
}) => {
  // The state only paints the highlight. The live drag sits in a ref, written
  // synchronously, so a mouseup that lands before React re-renders still sees
  // the last bar the pointer reached.
  const [selection, setSelection] = useState<Selection | null>(null);
  const selectionRef = useRef<Selection | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const spanMs = Math.max(range.end.getTime() - range.start.getTime(), 1);
  const rows = useMemo(() => normalizeBuckets(buckets, spanMs), [buckets, spanMs]);
  const gaps = useMemo(() => findGaps(rows), [rows]);
  const peak = useMemo(() => findPeak(rows), [rows]);
  const ticks = useMemo(() => axisTicks(rows), [rows]);
  const legend = useMemo(() => legendLevels(rows), [rows]);
  const maxTotal = useMemo(
    () => rows.reduce((max, r) => Math.max(max, r.total), 0),
    [rows]
  );
  const widthMs = useMemo(
    () =>
      bucketWidthMs(
        rows.map((r) => r.start.getTime()),
        spanMs
      ),
    [rows, spanMs]
  );

  const updateSelection = (next: Selection | null) => {
    selectionRef.current = next;
    setSelection(next);
  };

  // The bar under the pointer by x alone, or null when it is off the grid.
  const barIndexAtX = useCallback(
    (clientX: number): number | null => {
      const rect = gridRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0) return null;
      if (clientX < rect.left || clientX > rect.right) return null;
      const index = Math.floor(((clientX - rect.left) / rect.width) * rows.length);
      return Math.min(Math.max(index, 0), rows.length - 1);
    },
    [rows.length]
  );

  // The mouse button is released wherever the pointer ends up, not only over a bar.
  const commit = useCallback(
    (done: Selection) => {
      const { anchor, current } = done;
      const picksOnlyEmptyBar =
        anchor === current && rows[anchor]?.total === 0;
      if (picksOnlyEmptyBar) return;
      onRangeSelect(rangeBetweenBuckets(rows, anchor, current));
    },
    [rows, onRangeSelect]
  );

  // The listener goes up in the mousedown handler, not in an effect: an effect
  // only runs after the re-render, and a fast mouseup can beat it. It reads the
  // latest props through a ref so it can stay one stable function.
  const latestRef = useRef({ commit, barIndexAtX });
  latestRef.current = { commit, barIndexAtX };

  const release = useCallback((event: MouseEvent) => {
    window.removeEventListener("mouseup", release);
    const live = selectionRef.current;
    if (!live) return;
    const { commit: commitLatest, barIndexAtX: indexAtX } = latestRef.current;
    const current = indexAtX(event.clientX) ?? live.current;
    selectionRef.current = null;
    setSelection(null);
    commitLatest({ anchor: live.anchor, current });
  }, []);

  useEffect(() => () => window.removeEventListener("mouseup", release), [release]);

  const isSelected = (index: number): boolean =>
    selection !== null &&
    index >= Math.min(selection.anchor, selection.current) &&
    index <= Math.max(selection.anchor, selection.current);

  const summary = useMemo(() => countsSummary(rows), [rows]);
  const hasLogs = maxTotal > 0;
  const hovered = hoveredIndex === null ? null : rows[hoveredIndex];

  return (
    <GenericCard isCollapsible={false} shadow="sm">
      <div className="p-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3">
              <h2 className="text-sm font-semibold text-semantic-text-primary">
                Volume
              </h2>
              {summary && (
                <span className="text-xs text-semantic-text-secondary">{summary}</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-semantic-text-secondary">
              {rows.length > 0 && (
                <p>
                  {intervalLabel(widthMs)} intervals · drag to narrow the
                  range
                </p>
              )}
              {peak && (
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 bg-semantic-bg-warning-secondary text-semantic-text-warning">
                  <AlertTriangle size={12} aria-hidden="true" />
                  Peak {formatSpan(peak.bucket.start, peak.bucket.end)} ·{" "}
                  {peak.warning.toLocaleString()} WARNING,{" "}
                  {peak.error.toLocaleString()} ERROR
              </span>
              )}
              <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 bg-semantic-bg-tertiary text-semantic-text-secondary">
                <Clock size={12} aria-hidden="true" />
                Showing {formatSpan(range.start, range.end)}
              </span>
            </div>
          </div>
          {rows.length > 0 && (
            <ul className="flex flex-wrap gap-3" aria-label="Levels">
              {legend.map((level) => (
                <li
                  key={level}
                  className="flex items-center gap-1.5 text-xs text-semantic-text-secondary"
                >
                  <span
                    aria-hidden="true"
                    className={`inline-block w-2.5 h-2.5 rounded-sm ${LEVEL_BG_CLASS[level]}`}
                  />
                  {level}
                </li>
              ))}
            </ul>
          )}
        </div>

        {!hasLogs ? (
          <div
            className={`${CHART_HEIGHT_CLASS} flex items-center justify-center text-xs text-semantic-text-secondary`}
          >
            No logs in the selected range.
          </div>
        ) : (
          <>
            <div className="relative">
              <div
                ref={gridRef}
                role="group"
                aria-label="Log volume per interval"
                className={`grid gap-[2px] select-none ${CHART_HEIGHT_CLASS}`}
                style={{
                  gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))`,
                }}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {rows.map((row, index) => {
                  const heightPct = (row.total / maxTotal) * 100;
                  return (
                    <div
                      key={row.start.getTime()}
                      role="img"
                      aria-label={`${formatSpan(row.start, row.end)}: ${row.total} logs`}
                      className={`row-start-1 flex items-end cursor-crosshair rounded-sm ${
                        isSelected(index)
                          ? "bg-semantic-bg-brand-secondary outline outline-1 outline-semantic-border-brand-secondary"
                          : "hover:bg-semantic-bg-tertiary"
                      }`}
                      style={{ gridColumn: index + 1 }}
                      onMouseDown={(event) => {
                        if (event.button !== 0) return;
                        event.preventDefault();
                        updateSelection({ anchor: index, current: index });
                        window.addEventListener("mouseup", release);
                      }}
                      onMouseEnter={() => {
                        setHoveredIndex(index);
                        const live = selectionRef.current;
                        if (live) updateSelection({ ...live, current: index });
                      }}
                    >
                      <div
                        className="w-full flex flex-col-reverse rounded-t-sm overflow-hidden"
                        style={{
                          height: `${Math.max(heightPct, row.total > 0 ? 2 : 0)}%`,
                        }}
                      >
                        {VOLUME_LEVELS.map((level) =>
                          row.counts[level] > 0 ? (
                            <div
                              key={level}
                              className={LEVEL_BG_CLASS[level]}
                              style={{
                                height: `${(row.counts[level] / row.total) * 100}%`,
                              }}
                            />
                          ) : null
                        )}
                      </div>
                    </div>
                  );
                })}
                {gaps.map((gap) => (
                  <div
                    key={gap.firstIndex}
                    className="row-start-1 z-10 pointer-events-none flex items-center justify-center px-1 border border-dashed border-semantic-border-secondary rounded-sm bg-semantic-bg-primary text-[10px] text-semantic-text-secondary whitespace-nowrap overflow-hidden"
                    style={{
                      gridColumn: `${gap.firstIndex + 1} / ${gap.lastIndex + 2}`,
                    }}
                    title={`No logs · ${formatSpan(gap.start, gap.end)}`}
                  >
                    <span className="truncate">
                      No logs · {formatSpan(gap.start, gap.end)}
                    </span>
                  </div>
                ))}
              </div>

              {hovered && (
                <div className="absolute bottom-full mb-2 left-0 z-20 pointer-events-none">
                  {/* Inverse surface + inverse text flip together in dark mode. */}
                  <div className="bg-semantic-bg-inverse-secondary text-semantic-text-inverse-primary rounded-md shadow-lg px-3 py-2 text-xs min-w-[180px] border border-semantic-border-inverse-secondary">
                    <div className="font-mono text-[10px] opacity-80 mb-1">
                      {formatSpan(hovered.start, hovered.end)}
                    </div>
                    <div className="font-semibold mb-1">
                      {hovered.total.toLocaleString()}{" "}
                      {hovered.total === 1 ? "log" : "logs"}
                    </div>
                    {VOLUME_LEVELS.filter((l) => hovered.counts[l] > 0)
                      .reverse()
                      .map((level) => (
                        <div key={level} className="flex justify-between gap-3">
                          <span>{level}</span>
                          <span className="font-mono">
                            {hovered.counts[level].toLocaleString()}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between text-[10px] font-mono text-semantic-text-secondary">
              {ticks.map((tick) => (
                <span key={tick.getTime()}>{formatClock(tick)}</span>
              ))}
            </div>
          </>
        )}
      </div>
    </GenericCard>
  );
};

export default LogsVolumeHistogram;
